import { GlobeAltIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-responsive-modal";
import useCompanyProfile from "../hooks/useCompanyProfile";
import useTextTruncation from "../hooks/useTextTruncation";
import { type RootState } from "../redux/configureStore";
import { closeModal } from "../redux/features/memberModalSlice";
import Loading from "./Loading";
import MarkDown from "./MarkDown";

export default function MemberPopup() {
  const dispatch = useDispatch();
  const { open, name } = useSelector((state: RootState) => state.memberModal);
  const close = () => {
    dispatch(closeModal());
  };
  const [memberData, isLoading] = useCompanyProfile(name, true);
  const truncatedText = useTextTruncation(memberData?.description_summary || "", 200);

  return (
    <Modal
      open={open}
      onClose={close}
      center
      classNames={{
        overlay: "fixed inset-0 bg-black bg-opacity-50 flex items-start justify-start",
        modalContainer: "lg:px-20 xl:px-30 grid grid-cols-4 lg:grid-cols-12 gap-4 lg:gap-7.5",
        modal:
          "bg-white max-w-full aspect-video m-0 p-0 col-span-4 px-6 xl:px-0 lg:col-span-8 mt-[60px] md:mt-16 lg:mt-[99px] xl:mt-[100px] 2xl:mt-[106px] 3xl:mt-[110px] shadow-none",
      }}
      focusTrapped={false}
      closeIcon={<XMarkIcon className="none xl:h-6 xl:w-6 text-cultiv-grey xl:mr-8 xl:mt-10" />}
    >
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-full p-2 sm:p-8">
          <Loading />
        </div>
      ) : (
        <div className="w-full h-full lg:px-12 lg:py-12 grid grid-cols-6 lg:grid-cols-8 gap-4 lg:gap-8">
          <div className="w-full flex col-span-2 lg:col-span-4 item-center justify-center">
            <img
              src={memberData?.profile_picture}
              alt={memberData?.name}
              className="aspect-square object-contain"
            />
          </div>

          <div className="grid grid-cols-subgrid col-span-4 lg:col-span-4 items-center content-center gap-2 2xl:gap-8">
            <div className="flex col-span-full items-center gap-4">
              <h1 className="sm:text-lg md:text-2xl xl:text-4xl font-bold font-expandedBold">
                {memberData?.name}
              </h1>
              {memberData?.link && (
                <a href={memberData.link} target="_blank">
                  <GlobeAltIcon className="size-[18px] lg:size-8 hover:text-cultiv-yellow ease-in-out duration-300 transition-colors" />
                </a>
              )}
            </div>

            <MarkDown
              articleBody={truncatedText}
              proseParagraph="col-span-full prose-p:text-xs sm:prose-p:text-base xl:prose-p:text-xl 3xl:prose-p:text-2xl"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
